/**
 * Persistencia del estado meta en localStorage, con clave versionada y un
 * punto de migración para cuando cambie la forma del save en el futuro.
 *
 * Lógica pura: no depende de Phaser ni del DOM más allá de la interfaz
 * mínima de `Storage`, y acepta una implementación inyectada para poder
 * testear sin un `localStorage` real (útil en Node/Vitest).
 */

import { createDefaultMetaState } from './state';
import type { MetaState } from './types';

export const SAVE_KEY = 'vyv:save:v1';
export const CURRENT_SAVE_VERSION = 1 as const;

/** Subconjunto de la interfaz `Storage` del DOM que realmente usamos. */
export interface SaveStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Storage en memoria — fallback para entornos sin `localStorage` (tests, SSR). */
export class MemoryStorage implements SaveStorage {
  private data = new Map<string, string>();

  getItem(key: string): string | null {
    return this.data.has(key) ? (this.data.get(key) as string) : null;
  }

  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }

  removeItem(key: string): void {
    this.data.delete(key);
  }
}

let memoryFallback: MemoryStorage | undefined;

/** Devuelve `localStorage` si existe (navegador), o un fallback en memoria. */
export function getDefaultStorage(): SaveStorage {
  if (typeof localStorage !== 'undefined') {
    return localStorage;
  }
  memoryFallback ??= new MemoryStorage();
  return memoryFallback;
}

/**
 * Migra un save crudo (de versión desconocida) a la forma actual de
 * `MetaState`. Por ahora solo existe la versión 1, así que esto es un stub:
 * cuando aparezca `vyv:save:v2`, se agregan los pasos de migración aquí
 * encadenados (v1 -> v2 -> v3 -> ...).
 */
export function migrateSave(raw: unknown): MetaState {
  if (!raw || typeof raw !== 'object') {
    return createDefaultMetaState();
  }
  const data = raw as Partial<MetaState> & { saveVersion?: number };

  // Versión desconocida o futura: no sabemos migrarla, arrancamos de cero
  // en vez de arriesgar un estado corrupto.
  if (data.saveVersion !== CURRENT_SAVE_VERSION) {
    return createDefaultMetaState();
  }

  const defaults = createDefaultMetaState();
  return {
    coins: typeof data.coins === 'number' ? data.coins : defaults.coins,
    gems: typeof data.gems === 'number' ? data.gems : defaults.gems,
    xp: typeof data.xp === 'number' ? data.xp : defaults.xp,
    level: typeof data.level === 'number' ? data.level : defaults.level,
    upgrades: data.upgrades ? { ...defaults.upgrades, ...data.upgrades } : defaults.upgrades,
    gear: Array.isArray(data.gear) ? data.gear : defaults.gear,
    equipped: data.equipped ? { ...defaults.equipped, ...data.equipped } : defaults.equipped,
    chapterProgress:
      typeof data.chapterProgress === 'number' ? data.chapterProgress : defaults.chapterProgress,
    saveVersion: CURRENT_SAVE_VERSION,
  };
}

/** Carga el estado meta guardado, o crea uno nuevo si no hay save válido. */
export function loadMetaState(storage: SaveStorage = getDefaultStorage()): MetaState {
  const raw = storage.getItem(SAVE_KEY);
  if (!raw) {
    return createDefaultMetaState();
  }
  try {
    const parsed = JSON.parse(raw);
    return migrateSave(parsed);
  } catch {
    // JSON corrupto: no tumbamos el juego, arrancamos de cero.
    return createDefaultMetaState();
  }
}

/** Guarda el estado meta. Se llama tras cada evento de meta (autosave). */
export function saveMetaState(meta: MetaState, storage: SaveStorage = getDefaultStorage()): void {
  storage.setItem(SAVE_KEY, JSON.stringify(meta));
}

/** Borra el save (usado en debug/reset). */
export function clearSave(storage: SaveStorage = getDefaultStorage()): void {
  storage.removeItem(SAVE_KEY);
}
